import { FlatListWithTabs } from 'app/atoms/flatlist-with-tabs';
import { withErrorBoundary } from 'app/components/error-boundary';
import { Tabs } from 'app/components/tabs';
import { Text } from 'app/design/primitives/text';

import { useScopedTranslation } from 'app/hooks/i18n';
import { useNavigationOptions } from 'app/hooks/navigation';
import { useDeviceWidth } from 'app/hooks/use-device-size';
import { withDashboardLayout } from 'app/hooks/with-dashboard-layout';
import { formatCurrency } from 'app/localization/i18n';
import { LoanFilterKey, useAllLoans } from 'app/network/loan';
import { useUser, useUserCurrency } from 'app/network/user';
import { Currency } from 'app/schemas/user';
import { createTheme as createDSTheme } from 'app/shopify';
import type { Category } from 'app/types/category';
import { TransactionMode } from 'app/types/listing';
import { LoanType, type Loan } from 'app/types/loan';
import type { NavigationScreen } from 'app/types/navigation';
import { FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import { useRegisterLoading } from 'app/utils/performance/screen-perf-context';
import { withOptionalMetrics } from 'app/utils/performance/with-metrics';
import { isMobileNative, useAlignmentIfDesktop, usePageTitle, useUISize } from 'app/utils/web-helpers';
import { useEffect, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { filterLoansByTransactionMode } from './financial-impact-filters';
import { SavingsContent } from './savings-content';
const theme = createDSTheme();
const SAVINGS_CONTENT_CONTAINER_STYLE = { paddingTop: theme.spacing['spacing.general.x2'] };

/**
 * Calculate the total price for a specific category.
 *
 * @param loans - Array of Loan objects
 * @param categoryId - ID of the category to sum
 * @returns sum of prices for the specified category
 */
function sumPricesForCategory(loans: Loan[], categoryId: string): number {
  return loans.reduce<number>(
    (total, { listing }) => (listing.categoryPrimary.id === categoryId ? total + (listing.estimatedPrice ?? 0) : total),
    0,
  );
}

/**
 * Get the total amount saved in all categories of the specified loans.
 *
 * @param loans - The loans to analyze.
 * @returns An array of objects, each containing a category and the total amount saved in that category.
 */
function getTotalSaved(loans?: Loan[]) {
  // If no loans, return immediately
  if (!loans) return;

  const categories = loans.map((loan) => loan.listing.categoryPrimary);

  // Get unique categories
  const uniqueCategories = categories.filter((cat, index) => categories.findIndex((c) => c.id === cat.id) === index);

  // Map each category to an object containing the category and the total amount saved in that category
  const savedAmounts = uniqueCategories.map((category) => ({
    category,
    amountSaved: sumPricesForCategory(loans, category.id),
  }));

  // Sort by amount saved, in descending order
  return savedAmounts.sort((a, b) => b.amountSaved - a.amountSaved);
}

const MyFinancialImpactScreenComponent: NavigationScreen<'my-impact-financial'> = ({ route, navigation }) => {
  const { t } = useScopedTranslation();

  const { isLargeUI } = useUISize();
  const { data: user } = useUser();
  const setPageTitle = usePageTitle('my-impact-financial');
  useEffect(() => setPageTitle('title'), [setPageTitle]);

  const transactionModeFiltersEnabled = isFeatureEnabled(FeatureFlag.transactionMode, user?.featureFlags);
  const routeTransactionMode = route.params?.transactionMode ?? TransactionMode.lend;
  const [transactionModeState, setTransactionModeState] = useState<{
    source: TransactionMode;
    value: TransactionMode;
  }>(() => ({ source: routeTransactionMode, value: routeTransactionMode }));
  let currentTransactionModeState = transactionModeState;
  if (transactionModeState.source !== routeTransactionMode) {
    currentTransactionModeState = { source: routeTransactionMode, value: routeTransactionMode };
    setTransactionModeState(currentTransactionModeState);
  }
  const transactionMode = currentTransactionModeState.value;
  const setTransactionMode = (value: TransactionMode) =>
    setTransactionModeState({ source: routeTransactionMode, value });

  // Load completed loans from the API
  const myBorrowingsQuery = useAllLoans(LoanFilterKey.Completed, LoanType.Borrowing, { throwOnError: true });
  const myLendingsQuery = useAllLoans(LoanFilterKey.Completed, LoanType.Lending, { throwOnError: true });

  // Compute per category totals
  const borrowingLoans = transactionModeFiltersEnabled
    ? filterLoansByTransactionMode(myBorrowingsQuery.data, transactionMode)
    : myBorrowingsQuery.data;
  const lendingLoans = transactionModeFiltersEnabled
    ? filterLoansByTransactionMode(myLendingsQuery.data, transactionMode)
    : myLendingsQuery.data;
  const savedPerCategory = getTotalSaved(borrowingLoans);
  const othersSavedPerCategory = getTotalSaved(lendingLoans);

  const MySavings = (() => {
    return (
      <Savings
        savedPerCategory={savedPerCategory}
        transactionMode={transactionModeFiltersEnabled ? transactionMode : undefined}
        type={LoanType.Borrowing}
      />
    );
  })();

  const OthersSavings = (() => {
    return (
      <Savings
        savedPerCategory={othersSavedPerCategory}
        transactionMode={transactionModeFiltersEnabled ? transactionMode : undefined}
        type={LoanType.Lending}
      />
    );
  })();

  const tabs = {
    [LoanType.Borrowing]: MySavings,
    [LoanType.Lending]: OthersSavings,
  };

  const transactionModeTabs = [
    { label: t(`transactionTabs.${TransactionMode.lend}`), value: TransactionMode.lend },
    { label: t(`transactionTabs.${TransactionMode.sell}`), value: TransactionMode.sell },
    { label: t(`transactionTabs.${TransactionMode.give}`), value: TransactionMode.give },
  ];

  const roleTabs = [LoanType.Borrowing, LoanType.Lending].map((type) => ({
    label: transactionModeFiltersEnabled ? t(`roleTabs.${transactionMode}.${type}`) : t(`tabs.${type}`),
    value: type,
  }));

  useNavigationOptions(
    navigation,
    {
      headerTitle: () => (
        <Text variant="bodyXLargeMedium" color="white-label.header.on-header">
          {t(`title`)}
        </Text>
      ),
    },
    false,
  );

  const alignment = useAlignmentIfDesktop();

  useRegisterLoading(myBorrowingsQuery.isPending, 'my-impact-financial-borrowings');
  useRegisterLoading(myLendingsQuery.isPending, 'my-impact-financial-lendings');

  return (
    <View style={{ flex: 1 }}>
      {!isMobileNative ? (
        <Text
          fontWeight="600"
          marginBottom={isLargeUI ? 'spacing.general.x8' : 'spacing.general.x4'}
          marginTop="spacing.general.x5"
          marginHorizontal="spacing.general.x6"
          textAlign={alignment}
          variant="headerXSmall"
        >
          {t('title')}
        </Text>
      ) : null}

      <FlatListWithTabs
        initialTab={route.params?.type ?? LoanType.Borrowing}
        screenName="my-impact-financial"
        tabs={tabs}
        tabsDefinition={roleTabs}
        tabsVariant={transactionModeFiltersEnabled ? 'toggle' : 'default'}
        viewAboveTabs={
          transactionModeFiltersEnabled ? (
            <Tabs onPress={(value) => setTransactionMode(value)} tabs={transactionModeTabs} value={transactionMode} />
          ) : undefined
        }
      />
    </View>
  );
};

export const MyFinancialImpactScreen: NavigationScreen<'my-impact-financial'> = withOptionalMetrics(
  'MyFinancialImpactScreen',
  withErrorBoundary(withDashboardLayout(MyFinancialImpactScreenComponent)),
);

interface SavingsProps {
  savedPerCategory: undefined | { category: Category; amountSaved: number }[];
  transactionMode?: TransactionMode;
  type: LoanType;
}

function getCurrencyConversionRate(userCurrency: ReturnType<typeof useUserCurrency>) {
  if (userCurrency === Currency.EUR) return 1.5;
  if (userCurrency === Currency.USD) return 1.4;
  return 1;
}

function getChartLayout(deviceWidth: number, isLargeUI: boolean) {
  const maxGraphSize = isLargeUI ? 280 : 400;
  const containerMargin = theme.spacing['spacing.general.x4'];
  const dataViewMargin = theme.spacing['spacing.general.x5'];
  let chartMargin = theme.spacing['spacing.general.x4'];
  let graphSize = deviceWidth - 2 * containerMargin - 2 * dataViewMargin - 2 * chartMargin;
  if (graphSize > maxGraphSize) {
    chartMargin += (graphSize - maxGraphSize) / 2;
    graphSize = maxGraphSize;
  }
  return { chartMargin, containerMargin, dataViewMargin, graphSize };
}

const Savings = ({ savedPerCategory, transactionMode, type }: SavingsProps) => {
  const [howItWorksVisible, setHowItWorksVisible] = useState(false);
  const userCurrency = useUserCurrency();
  const conversionRate = getCurrencyConversionRate(userCurrency);

  const savedPerCategory2 = savedPerCategory
    ?.map((saved) => ({
      ...saved,
      amountSaved: saved.amountSaved / conversionRate,
      value: saved.amountSaved / conversionRate,
      color: saved.category.colorMedium,
    }))
    .reverse();

  const totalSaved = savedPerCategory2?.reduce((total, { amountSaved }) => total + amountSaved, 0) ?? 0;

  const deviceWidth = useDeviceWidth();
  const { isLargeUI } = useUISize();
  const chartLayout = getChartLayout(deviceWidth, isLargeUI);

  const formattedTotal = formatCurrency(Math.round(totalSaved), userCurrency, 0);
  const numDigits = formattedTotal.length;
  const graphTitleFontSize = ((70 - numDigits * 2.7) * chartLayout.graphSize) / 300;

  return (
    <ScrollView style={{ flex: 1, width: deviceWidth }} contentContainerStyle={SAVINGS_CONTENT_CONTAINER_STYLE}>
      <SavingsContent
        {...chartLayout}
        deviceWidth={deviceWidth}
        formattedTotal={formattedTotal}
        graphTitleFontSize={graphTitleFontSize}
        howItWorksVisible={howItWorksVisible}
        savedPerCategory={savedPerCategory2}
        setHowItWorksVisible={setHowItWorksVisible}
        totalSaved={totalSaved}
        transactionMode={transactionMode}
        type={type}
        userCurrency={userCurrency}
      />
    </ScrollView>
  );
};
